import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrIcon from "./GrIcon";
import GrIconButton from "./GrIconButton";
import icons from "../icons";
import googlePlayLogo from "src/assets/images/icons/google-play.svg";
import { gr } from "../tokens";

// The logged-out screens (login, forgot password), one screen tall (no page scroll from tablets up):
// a full-height brand side on the left (teal gradient, no card: brand wordmark or optional image tile,
// the heading, optional highlights, the store badges centred at the bottom) and the form on white on
// the right, centred. Phones show only the form; the store badges then sit under it.
// `title` / `subtitle` may hold the gym's own HTML (login texts from the admin): headings and paragraphs
// inside them take the title / subtitle look, so admin markup and the legacy global h1–h3 rules
// (new.css `h1 { 33px !important }`, h3 background) never change the size.
const IMAGE_WIDTH = 275;
const IMAGE_HEIGHT = 250;
// Logo tile width on the brand side (only when `image` is used without `brandMark`).
const LOGO_WIDTH = 120;
const LOGO_WIDTH_SHORT = 96;
// Line length of the brand side's heading, description and cards on wide screens.
const BRAND_TEXT_MAX_WIDTH = 720;
// Description a touch shorter than the headline so the line doesn't stretch edge-to-edge.
const BRAND_LINE_MAX_WIDTH = 640;
// Two decorative circles behind the brand content (size, and how far they sit past the edge).
const CIRCLE_LARGE = 420;
const CIRCLE_SMALL = 320;
const CIRCLE_OFFSET = 140;
// Space between the feature items, the same across and down so the 2×2 grid stays even (56px).
const FEATURE_GAP = `calc(${gr.space[12]} + ${gr.space[2]})`;
// A short window (laptop with browser bars): smaller type and tighter gaps, so the brand side never scrolls.
const SHORT_SCREEN = "@media (max-height: 760px)";
// Brand logo above the form (graphic mark; the teal side carries the wordmark).
const FORM_LOGO_HEIGHT = 88;
const FORM_LOGO_HEIGHT_SHORT = 72;
const FORM_MAX_WIDTH = 440;

// Admin HTML inside the title / subtitle follows the wrapper's text style.
const inheritTextSx = {
  "& h1, & h2, & h3, & h4, & h5, & h6, & p, & div, & span": {
    m: 0,
    p: 0,
    fontFamily: "inherit",
    fontSize: "inherit !important",
    fontWeight: "inherit",
    lineHeight: "inherit",
    color: "inherit",
    background: "transparent",
  },
};

function BrandImage({ src, alt, placeholderIcon }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  const frameSx = {
    display: "block",
    width: "100%",
    height: "auto",
    aspectRatio: `${IMAGE_WIDTH} / ${IMAGE_HEIGHT}`,
  };

  // The gym's image (usually its logo) sits on a white tile, so a teal logo stays readable on the teal
  // side. Missing or broken image: a soft icon instead (never the browser's broken image + alt text).
  return (
    <Box
      sx={{
        width: LOGO_WIDTH,
        maxWidth: "100%",
        p: gr.space[3],
        [SHORT_SCREEN]: { width: LOGO_WIDTH_SHORT },
        borderRadius: gr.radius.tile,
        background: gr.color.surface,
        color: gr.color.brand,
      }}
    >
      {!src || failed ? (
        <Box
          aria-hidden
          sx={{
            ...frameSx,
            display: "grid",
            placeItems: "center",
            "& > svg": { width: gr.space[12], height: gr.space[12] },
          }}
        >
          <GrIcon icon={placeholderIcon} />
        </Box>
      ) : (
        <Box
          component="img"
          src={src}
          alt={alt}
          width={IMAGE_WIDTH}
          height={IMAGE_HEIGHT}
          onError={() => setFailed(true)}
          sx={{ ...frameSx, objectFit: "contain" }}
        />
      )}
    </Box>
  );
}

BrandImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  placeholderIcon: PropTypes.object.isRequired,
};

// Wordmark on the teal side. String "Name by …" → Name strong + suffix soft; otherwise render as-is.
function BrandMark({ children, name, suffix }) {
  const primary =
    name ||
    (typeof children === "string" ? children.replace(/\s+by\s+.*/i, "") : null);
  const secondary =
    suffix ||
    (typeof children === "string"
      ? (children.match(/\s+(by\s+.+)$/i) || [])[1]
      : null);
  const split = primary && secondary;

  return (
    <Box
      component="p"
      sx={{
        m: 0,
        fontFamily: gr.font.family,
        // Stronger than the old tile caption, still under the page headline (h1 → display).
        fontSize: { md: gr.font.size.h1, lg: gr.font.size.h1 },
        fontWeight: gr.font.weight.semibold,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
        color: "inherit",
      }}
    >
      {split ? (
        <>
          <Box component="span">{primary}</Box>
          <Box
            component="span"
            sx={{
              ml: gr.space[2],
              fontWeight: gr.font.weight.regular,
              fontSize: "0.55em",
              letterSpacing: 0,
              color: gr.color.brandSoft,
              verticalAlign: "baseline",
            }}
          >
            {secondary}
          </Box>
        </>
      ) : (
        children
      )}
    </Box>
  );
}

BrandMark.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string,
  suffix: PropTypes.string,
};

// Short benefit list on the teal side: a glass icon tile + one line each, in white.
// Items with a `title` are feature items in two columns (glass icon tile, title, one line); plain items
// are a simple list.

function Highlights({ items }) {
  const cards = items.some((item) => item.title);
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "grid",
        gridTemplateColumns: cards
          ? "repeat(2, minmax(0, 1fr))"
          : "minmax(0, 1fr)",
        // Roomy on tall screens; the compact gaps keep a short window from scrolling.
        columnGap: FEATURE_GAP,
        rowGap: cards ? FEATURE_GAP : gr.space[3],
        [SHORT_SCREEN]: {
          columnGap: gr.space[10],
          rowGap: cards ? gr.space[8] : gr.space[2],
        },
      }}
    >
      {items.map((item) => (
        <Box
          component="li"
          key={item.title || item.text}
          sx={{
            display: "flex",
            alignItems: cards ? "flex-start" : "center",
            gap: gr.space[4],
            [SHORT_SCREEN]: { gap: gr.space[3] },
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "grid",
              placeItems: "center",
              flex: "none",
              width: gr.control.lg,
              height: gr.control.lg,
              borderRadius: gr.radius.tile,
              background: gr.color.onBrandGlass,
              boxShadow: `inset 0 0 0 1px ${gr.color.onBrandGlassLine}`,
              "& > svg": { width: gr.icon.xl, height: gr.icon.xl },
              [SHORT_SCREEN]: {
                width: gr.control.touch,
                height: gr.control.touch,
                "& > svg": { width: gr.icon.lg, height: gr.icon.lg },
              },
            }}
          >
            <GrIcon icon={item.icon} />
          </Box>
          {item.title ? (
            <Box
              component="span"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: gr.space[1],
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: gr.font.size.lg,
                  fontWeight: gr.font.weight.semibold,
                }}
              >
                {item.title}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: gr.font.size.md,
                  lineHeight: gr.font.lineHeight.normal,
                  color: gr.color.brandSoft,
                }}
              >
                {item.text}
              </Box>
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                fontSize: gr.font.size.lg,
                lineHeight: gr.font.lineHeight.normal,
                [SHORT_SCREEN]: { fontSize: gr.font.size.md },
              }}
            >
              {item.text}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

Highlights.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.object.isRequired,
      title: PropTypes.string,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
};

// The mobile app: two store-style badges (App Store, Google Play) with a small "Coming soon" tag on each
// corner. Drawn in the portal's own style, not the stores' official artwork, and not links: the stores
// allow their badges only for an app that is live or on pre-order. Swap in the official badges and the
// store links when the app is published.
const STORES = [
  { key: "google-play", image: googlePlayLogo, name: "Google Play" },
  { key: "app-store", icon: icons.apple, name: "App Store" },
];

// A store badge in the stores' familiar shape (dark, logo + "Get it on" + store name), drawn here, not
// the stores' official artwork, and not a link: the stores allow their badges only for a live app.
// Swap in the official badges and links when the app is published.
function StoreBadge({ icon, image, name }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: gr.space[3],
        height: gr.control.lg,
        pl: gr.space[3],
        pr: gr.space[4],
        borderRadius: gr.radius.control,
        background: gr.color.ink,
        color: gr.color.onBrand,
        boxShadow: `inset 0 0 0 1px ${gr.color.onBrandGlassLine}`,
        whiteSpace: "nowrap",
        "& > svg, & > img": {
          width: gr.icon.xl,
          height: gr.icon.xl,
          flex: "none",
        },
        [SHORT_SCREEN]: {
          height: gr.control.md,
          gap: gr.space[2],
          pl: gr.space[2],
          pr: gr.space[3],
          "& > svg, & > img": {
            width: gr.icon.lg,
            height: gr.icon.lg,
          },
        },
      }}
    >
      {image ? (
        <img src={image} alt="" width={24} height={24} />
      ) : (
        <GrIcon icon={icon} />
      )}
      <Box
        component="span"
        sx={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}
      >
        <Box
          component="span"
          sx={{
            fontSize: gr.font.size.xs,
            opacity: 0.8,
            [SHORT_SCREEN]: { fontSize: 10 },
          }}
        >
          Get it on
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: gr.font.size.lg,
            fontWeight: gr.font.weight.semibold,
            [SHORT_SCREEN]: { fontSize: gr.font.size.md },
          }}
        >
          {name}
        </Box>
      </Box>
    </Box>
  );
}

StoreBadge.propTypes = {
  icon: PropTypes.object,
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
};

// "Coming soon" as a quiet label between two hairlines, spanning the width of the badge pair, so it
// reads as a heading for the badges (not a pill floating on its own).
function AppStrip({ align, tone }) {
  const glass = tone === "glass";
  const hairline = {
    flex: 1,
    minWidth: gr.space[6],
    borderTop: `1px solid ${glass ? gr.color.onBrandGlassLine : gr.color.line}`,
  };
  return (
    <Box
      role="group"
      aria-label="Mobile app coming soon"
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "stretch",
        alignSelf: align,
        gap: gr.space[3],
        [SHORT_SCREEN]: { gap: gr.space[2] },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: gr.space[3],
          color: glass ? gr.color.brandSoft : gr.color.muted,
          fontSize: gr.font.size.sm,
          fontWeight: gr.font.weight.medium,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}
      >
        <Box component="span" aria-hidden sx={hairline} />
        Coming soon
        <Box component="span" aria-hidden sx={hairline} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: gr.space[3],
          [SHORT_SCREEN]: { gap: gr.space[2] },
        }}
      >
        {STORES.map((store) => (
          <StoreBadge key={store.key} icon={store.icon} image={store.image} name={store.name} />
        ))}
      </Box>
    </Box>
  );
}

AppStrip.propTypes = {
  align: PropTypes.oneOf(["flex-start", "center"]).isRequired,
  tone: PropTypes.oneOf(["glass", "solid"]).isRequired,
};

// Show / hide button at the end of a password field (InputAdornment position="end"). The label says what
// a click does ("Show password" / "Hide password"); the icon shows the current state.
export function GrPasswordToggle({ shown, onToggle }) {
  return (
    <GrIconButton
      variant="ghost"
      size="sm"
      aria-label={shown ? "Hide password" : "Show password"}
      aria-pressed={shown}
      onClick={onToggle}
      edge="end"
    >
      <GrIcon icon={shown ? icons.eye : icons.eyeOff} />
    </GrIconButton>
  );
}

GrPasswordToggle.propTypes = {
  shown: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function GrAuthLayout({
  heading,
  brandMark,
  brandMarkName,
  brandMarkSuffix,
  image,
  imageAlt = "",
  placeholderIcon = icons.image,
  description,
  highlights,
  appStrip,
  formLogo,
  legal,
  title,
  subtitle,
  footer,
  children,
  sx,
}) {
  // `true` → the default store badges; a node replaces them.
  const strip = (align, tone) =>
    appStrip === true ? <AppStrip align={align} tone={tone} /> : appStrip;

  return (
    <Box
      sx={{
        display: "grid",
        // Brand side the larger part (about 60%), the form on the rest.
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "minmax(0, 7fr) minmax(0, 5fr)",
        },
        // One screen tall from tablets up (no page scroll); only the form side scrolls on a very short
        // window. Phones: the page grows with the form.
        minHeight: "100dvh",
        height: { md: "100dvh" },
        background: gr.color.surface,
        fontFamily: gr.font.family,
        ...sx,
      }}
    >
      <Box
        component="aside"
        sx={{
          position: "relative",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          // Centre the brand column + app strip in the teal panel (not left-stuck).
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
          overflow: "hidden",
          px: { md: gr.space[10], lg: gr.space[12] },
          py: gr.space[10],
          [SHORT_SCREEN]: { py: gr.space[6] },
          // Deep teal only (brand-dark → brand-strong): white text stays above 4.5:1 everywhere on it.
          background: `linear-gradient(160deg, ${gr.color.brandDark} 0%, ${gr.color.brandStrong} 100%)`,
          color: gr.color.onBrand,
          // Two soft circles give the flat teal some depth (decorative, behind the content).
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            borderRadius: "50%",
            background: gr.color.onBrandGlass,
            pointerEvents: "none",
          },
          "&::before": {
            width: CIRCLE_LARGE,
            height: CIRCLE_LARGE,
            top: -CIRCLE_OFFSET,
            right: -CIRCLE_OFFSET,
          },
          "&::after": {
            width: CIRCLE_SMALL,
            height: CIRCLE_SMALL,
            bottom: -CIRCLE_OFFSET,
            left: -CIRCLE_OFFSET,
          },
        }}
      >
        {/* Intro (wordmark + headline) and features as one block, centred in the panel; large type
            and roomy gaps fill it without leaving a hole between the two. */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: gr.space[12],
            width: "100%",
            maxWidth: BRAND_TEXT_MAX_WIDTH,
            [SHORT_SCREEN]: { gap: gr.space[10] },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: gr.space[8],
              [SHORT_SCREEN]: { gap: gr.space[4] },
            }}
          >
            {brandMark || brandMarkName ? (
              <BrandMark name={brandMarkName} suffix={brandMarkSuffix}>
                {brandMark}
              </BrandMark>
            ) : (
              <BrandImage
                src={image}
                alt={imageAlt}
                placeholderIcon={placeholderIcon}
              />
            )}
            {(heading || description) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: gr.space[4],
                  [SHORT_SCREEN]: { gap: gr.space[3] },
                }}
              >
                {heading ? (
                  <Box
                    component="h2"
                    sx={{
                      m: 0,
                      fontFamily: gr.font.family,
                      fontSize: {
                        md: gr.font.size.display,
                        lg: gr.font.size.hero,
                      },
                      fontWeight: gr.font.weight.semibold,
                      lineHeight: 1.15,
                      [SHORT_SCREEN]: { fontSize: gr.font.size.display },
                      letterSpacing: "-0.02em",
                      color: "inherit",
                    }}
                  >
                    {heading}
                  </Box>
                ) : null}
                {description ? (
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      maxWidth: BRAND_LINE_MAX_WIDTH,
                      fontSize: { md: gr.font.size.xl, lg: gr.font.size.xxl },
                      lineHeight: gr.font.lineHeight.normal,
                      color: gr.color.brandSoft,
                      [SHORT_SCREEN]: { fontSize: gr.font.size.lg },
                    }}
                  >
                    {description}
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
          {highlights?.length ? <Highlights items={highlights} /> : null}
        </Box>
        {appStrip ? (
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              pt: gr.space[8],
              [SHORT_SCREEN]: { pt: gr.space[4] },
            }}
          >
            {strip("center", "glass")}
          </Box>
        ) : null}
      </Box>

      <Box
        component="main"
        sx={{
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflowY: { md: "auto" },
          px: gr.space[4],
          py: gr.space[10],
        }}
      >
        <Box sx={{ width: "100%", maxWidth: FORM_MAX_WIDTH }}>
          {/* Logo + title as one header block so phone and desktop spacing match. */}
          {formLogo || title || subtitle ? (
            <Box
              component="header"
              sx={{
                mb: gr.space[6],
                // Under a centred logo, keep title/subtitle centred on phones (same unit as desktop).
                textAlign: formLogo ? { xs: "center", md: "left" } : "left",
              }}
            >
              {formLogo ? (
                <Box
                  component="img"
                  src={formLogo}
                  alt=""
                  width={FORM_LOGO_HEIGHT}
                  height={FORM_LOGO_HEIGHT}
                  sx={{
                    display: "block",
                    width: "auto",
                    maxWidth: "100%",
                    height: FORM_LOGO_HEIGHT,
                    objectFit: "contain",
                    objectPosition: { xs: "center", md: "left" },
                    // Phones: centred with the title; from tablets up: left, in line with the title.
                    mx: { xs: "auto", md: 0 },
                    // Tighter than space[5] so logo + title read as one unit.
                    mb: title || subtitle ? gr.space[4] : 0,
                    [SHORT_SCREEN]: { height: FORM_LOGO_HEIGHT_SHORT },
                  }}
                />
              ) : null}
              {title ? (
                <Box
                  component="h1"
                  sx={{
                    m: 0,
                    fontFamily: gr.font.family,
                    // !important only to beat the legacy global `h1 { font-size: 33px !important }` (new.css).
                    fontSize: `${gr.font.size.h2} !important`,
                    fontWeight: gr.font.weight.medium,
                    lineHeight: gr.font.lineHeight.tight,
                    letterSpacing: "-0.01em",
                    color: gr.color.brandStrong,
                    overflowWrap: "anywhere",
                    "@media (max-width: 599.95px)": {
                      fontSize: `${gr.font.size.xxl} !important`,
                    },
                    ...inheritTextSx,
                  }}
                >
                  {title}
                </Box>
              ) : null}
              {subtitle ? (
                <Box
                  sx={{
                    mt: title ? gr.space[1] : 0,
                    fontSize: gr.font.size.md,
                    lineHeight: gr.font.lineHeight.normal,
                    color: gr.color.ink2,
                    overflowWrap: "anywhere",
                    ...inheritTextSx,
                  }}
                >
                  {subtitle}
                </Box>
              ) : null}
            </Box>
          ) : null}
          {children}
          {footer ? (
            <Box
              sx={{
                mt: gr.space[6],
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: gr.space[1],
                fontSize: gr.font.size.md,
                color: gr.color.ink2,
              }}
            >
              {footer}
            </Box>
          ) : null}
          {appStrip ? (
            <Box sx={{ display: { xs: "block", md: "none" }, mt: gr.space[8] }}>
              {strip("center", "solid")}
            </Box>
          ) : null}
          {legal ? (
            <Box
              component="p"
              sx={{
                m: 0,
                mt: gr.space[10],
                textAlign: "center",
                fontSize: gr.font.size.xs,
                color: gr.color.muted,
              }}
            >
              {legal}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

GrAuthLayout.propTypes = {
  /** Brand panel heading (desktop only), e.g. "Hi, Welcome Back". */
  heading: PropTypes.node,
  /** Wordmark on the brand side (e.g. "GymRite by BMS"); when set, `image` is not shown. */
  brandMark: PropTypes.node,
  /** Optional split: strong name (e.g. "GymRite"). */
  brandMarkName: PropTypes.string,
  /** Optional split: softer suffix (e.g. "by BMS"). */
  brandMarkSuffix: PropTypes.string,
  /** Brand panel image URL; missing or broken → `placeholderIcon`. Ignored when `brandMark` is set. */
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  placeholderIcon: PropTypes.object,
  /** Line under the brand heading (desktop only). */
  description: PropTypes.node,
  /**
   * Benefits on the brand side (desktop only): `{ icon, title?, text }` each, icon from `icons`.
   * With titles they are glass cards in two columns; without, a simple list.
   */
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.object.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  /**
   * `true` → Google Play and App Store badges under a "Coming soon" label between two hairlines,
   * centred at the bottom of the brand side; on phones they sit under the form, centred. A node replaces
   * the default badges.
   */
  appStrip: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  /** Form title (h1, 24/500 brand-strong; 20 on phones). May be the gym's HTML. */
  title: PropTypes.node,
  /** Line under the title (14 ink-2). May be the gym's HTML. */
  subtitle: PropTypes.node,
  /** Under the form, centred (e.g. "Back to Login? Login"). */
  footer: PropTypes.node,
  /** Brand logo URL above the form (graphic mark; teal side holds the wordmark on desktop). */
  formLogo: PropTypes.string,
  /** Small muted line at the very bottom of the form side (e.g. the gym's copyright text). */
  legal: PropTypes.node,
  /** The form. */
  children: PropTypes.node,
  sx: PropTypes.object,
};
